import {
  ChevronRightIcon,
  PaintBrushIcon,
  RectangleGroupIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  SwatchIcon
} from "@heroicons/react/24/outline"
import React, { useState } from "react"

// Import shape icons for preview
import shapeRoundIcon from "../../../../assets/images/builder/v2/shape-round.svg"
import shapeSharpIcon from "../../../../assets/images/builder/v2/shape-sharp.svg"
import shapeSoftIcon from "../../../../assets/images/builder/v2/shape-soft.svg"
import Typography from "../../../components/shared/Typography"
import { useWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

import ColorThemeForm from "./forms/ColorThemeForm"
import FontsForm from "./forms/FontsForm"
import FooterForm from "./forms/FooterForm"
import HeaderForm from "./forms/HeaderForm"
import ShapeForm from "./forms/ShapeForm"

const SitewideStylesForm = () => {
  const { theme, practice, handleUpdateTheme } = useWebsiteBuilderV2()
  const [selectedSection, setSelectedSection] = useState(null)

  // Color presets for preview - matching Figma design colors
  const colorPresets = [
    {
      id: "earthy",
      colors: ["#BD654F", "#A4786E", "#E6DAD7", "#F4ECE9", "#F8F6F5"]
    },
    {
      id: "zen",
      colors: ["#556B2F", "#6B8E23", "#9ACD32", "#ADFF2F", "#F0FFF0"]
    },
    {
      id: "tranquility",
      colors: ["#4682B4", "#87CEEB", "#B0E0E6", "#E0F6FF", "#F0F8FF"]
    },
    {
      id: "vitality",
      colors: ["#FF6347", "#FF4500", "#FF8C00", "#FFD700", "#FFFACD"]
    },
    {
      id: "awaken",
      colors: ["#FF7F50", "#FA8072", "#FFB6C1", "#FFC0CB", "#FFF0F5"]
    },
    {
      id: "flourish",
      colors: ["#228B22", "#32CD32", "#90EE90", "#98FB98", "#F0FFF0"]
    },
    {
      id: "radiance",
      colors: ["#DAA520", "#B8860B", "#F0E68C", "#FFF8DC", "#FFFAF0"]
    },
    {
      id: "harmony",
      colors: ["#008B8B", "#20B2AA", "#48D1CC", "#AFEEEE", "#F0FFFF"]
    },
    {
      id: "balance",
      colors: ["#191970", "#4169E1", "#87CEEB", "#B0E0E6", "#F0F8FF"]
    },
    {
      id: "serenity",
      colors: ["#4B0082", "#9370DB", "#DDA0DD", "#E6E6FA", "#F8F8FF"]
    }
  ]

  // Font pairs for preview - matching Figma design
  const fontPairs = [
    {
      id: "playfair-work",
      primary: "Playfair Display",
      secondary: "Work Sans",
      primaryFont: "Playfair Display, serif",
      secondaryFont: "Work Sans, sans-serif"
    },
    {
      id: "inter-libre",
      primary: "Inter",
      secondary: "Libre Franklin",
      primaryFont: "Inter, sans-serif",
      secondaryFont: "Libre Franklin, sans-serif"
    },
    {
      id: "poppins-roboto",
      primary: "Poppins",
      secondary: "Roboto",
      primaryFont: "Poppins, sans-serif",
      secondaryFont: "Roboto, sans-serif"
    },
    {
      id: "montserrat-open",
      primary: "Montserrat",
      secondary: "Open Sans",
      primaryFont: "Montserrat, sans-serif",
      secondaryFont: "Open Sans, sans-serif"
    },
    {
      id: "opensans-lato",
      primary: "Open Sans",
      secondary: "Lato",
      primaryFont: "Open Sans, sans-serif",
      secondaryFont: "Lato, sans-serif"
    },
    {
      id: "arima-madurai",
      primary: "Arima Madurai",
      secondary: "Source Sans Pro",
      primaryFont: "Arima Madurai, cursive",
      secondaryFont: "Source Sans Pro, sans-serif"
    },
    {
      id: "cabin-roboto",
      primary: "Cabin",
      secondary: "Roboto",
      primaryFont: "Cabin, sans-serif",
      secondaryFont: "Roboto, sans-serif"
    },
    {
      id: "ebgaramond-opensans",
      primary: "EB Garamond",
      secondary: "Open Sans",
      primaryFont: "EB Garamond, serif",
      secondaryFont: "Open Sans, sans-serif"
    },
    {
      id: "jost-inter",
      primary: "Jost",
      secondary: "Inter",
      primaryFont: "Jost, sans-serif",
      secondaryFont: "Inter, sans-serif"
    },
    {
      id: "nunito-opensans",
      primary: "Nunito",
      secondary: "Open Sans",
      primaryFont: "Nunito, sans-serif",
      secondaryFont: "Open Sans, sans-serif"
    }
  ]

  // Shape options for preview
  const shapeOptions = [
    {
      id: "sharp",
      name: "Sharp",
      borderRadius: "0px",
      buttonStyle: "square",
      icon: shapeSharpIcon
    },
    {
      id: "soft",
      name: "Soft",
      borderRadius: "12px",
      buttonStyle: "rounded",
      icon: shapeSoftIcon
    },
    {
      id: "round",
      name: "Round",
      borderRadius: "50px",
      buttonStyle: "pill",
      icon: shapeRoundIcon
    }
  ]

  // Helper functions to find current selections
  const findCurrentColorPreset = () => {
    if (theme?.colors?.selectedPreset) {
      return colorPresets.find((preset) => preset.id === theme.colors.selectedPreset)
    }

    // If no preset is selected, check if we have custom colors
    if (theme?.colors?.customHex) {
      return null // Custom colors
    }

    // Try to match current colors with a preset
    const currentColors = [
      theme?.colors?.primary,
      theme?.colors?.primaryDark,
      theme?.colors?.secondary,
      theme?.colors?.secondaryDark,
      theme?.colors?.accent
    ]

    return colorPresets.find((preset) =>
      currentColors.every((color, index) => {
        if (!color || !preset.colors[index]) return false
        const normalizedCurrent = color.replace("#", "").toUpperCase()
        const normalizedPreset = preset.colors[index].replace("#", "").toUpperCase()
        return normalizedCurrent === normalizedPreset
      })
    )
  }

  // Default to first pair
  const findCurrentFontPair = () =>
    fontPairs.find(
      (pair) => pair.primaryFont === theme?.fonts?.heading && pair.secondaryFont === theme?.fonts?.paragraph
    ) || fontPairs[0]

  // Default to "soft"
  const findCurrentShape = () =>
    shapeOptions.find(
      (option) => option.borderRadius === theme?.borderRadius && option.buttonStyle === theme?.buttonStyle
    ) || shapeOptions[1]

  const sections = [
    {
      id: "color-theme",
      title: "Color Theme",
      icon: <SwatchIcon className="h-5 w-5" />,
      component: ColorThemeForm,
      preview: () => {
        const currentPreset = findCurrentColorPreset()
        if (currentPreset) {
          return (
            <div className="flex items-center space-x-[-8px]">
              {currentPreset.colors.map((color, index) => (
                <div
                  key={index}
                  className="h-7 w-7 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )
        } else if (theme?.colors?.customHex) {
          // Show custom color preview
          return (
            <div className="flex items-center space-x-[-8px]">
              <div
                className="h-7 w-7 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: theme.colors.customHex }}
              />
            </div>
          )
        }
        return null
      }
    },
    {
      id: "fonts",
      title: "Fonts",
      icon: <PaintBrushIcon className="h-5 w-5" />,
      component: FontsForm,
      preview: () => {
        const currentPair = findCurrentFontPair()
        return (
          <div className="flex items-end space-x-1">
            <span className="text-2xl font-bold leading-none" style={{ fontFamily: currentPair.primaryFont }}>
              Ag
            </span>
            <span className="text-base leading-none" style={{ fontFamily: currentPair.secondaryFont }}>
              Ag
            </span>
          </div>
        )
      }
    },
    {
      id: "shape",
      title: "Shape",
      icon: <Squares2X2Icon className="h-5 w-5" />,
      component: ShapeForm,
      preview: () => {
        const currentShape = findCurrentShape()
        return (
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gray-200 p-2">
            <img src={currentShape.icon} alt={currentShape.name} className="h-3 w-3" />
          </div>
        )
      }
    },
    {
      id: "header",
      title: "Header",
      icon: <RectangleGroupIcon className="h-5 w-5" />,
      component: HeaderForm
    },
    {
      id: "footer",
      title: "Footer",
      icon: <RectangleStackIcon className="h-5 w-5" />,
      component: FooterForm
    }
  ]

  const handleSectionSelect = (sectionId) => {
    setSelectedSection(sectionId)
  }

  const handleBack = () => {
    setSelectedSection(null)
  }

  const handleUpdate = async (updates, options = {}) => {
    try {
      if (options.temporary) {
        // For temporary preview, just update the theme state without saving to database
        // This will be handled by the context's temporary preview system
        await handleUpdateTheme(updates, { temporary: true })
      } else {
        // For permanent changes, save to database
        await handleUpdateTheme(updates)
      }
    } catch (error) {
      // Error handling is done in the context
    }
  }

  // If a section is selected, show the form
  if (selectedSection) {
    const activeSection = sections.find((section) => section.id === selectedSection)
    const SectionComponent = activeSection.component

    return (
      <div className="overflow-hidden rounded-lg border border-gray-light bg-white">
        {/* Form Content */}
        <SectionComponent theme={theme} practice={practice} onUpdate={handleUpdate} onClose={handleBack} />
      </div>
    )
  }

  // Show the accordion items when no section is selected
  return (
    <div className="space-y-0 overflow-hidden rounded-lg border border-gray-light">
      {sections.map((section) => (
        <button
          key={section.id}
          className="flex w-full items-center justify-between border-b border-gray-light px-8 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-ultralight"
          onClick={() => handleSectionSelect(section.id)}>
          <div className="flex items-center">
            <Typography variant="body" className="font-medium text-gray-dark">
              {section.title}
            </Typography>
          </div>
          <div className="flex items-center space-x-3">
            {section.preview && section.preview()}
            <ChevronRightIcon className="h-5 w-5 text-gray" />
          </div>
        </button>
      ))}
    </div>
  )
}

export default SitewideStylesForm
